/**
 * Diagnostics produced by the tokenizer, the parser and the semantic analyser.
 *
 * Every diagnostic carries a machine-readable `code`, a human message, a source
 * range and — where we can offer one — a concrete `suggestion`. Codes are
 * stable identifiers used by tests and by the i18n layer; messages are the
 * English fallback and get translated at render time.
 */
import type { SourceRange } from '../ast/source';

export type DiagnosticSeverity = 'error' | 'warning' | 'info';

/**
 * Stable diagnostic codes.
 *
 * `LEX_*` come from the tokenizer, `SYN_*` from the parser, `SEM_*` from
 * semantic analysis, `LIM_*` from the safety limits.
 */
export const DiagnosticCode = {
  // Lexical
  UnterminatedString: 'LEX001',
  UnterminatedBlockComment: 'LEX002',
  UnexpectedCharacter: 'LEX003',

  // Syntax
  UnclosedBrace: 'SYN001',
  UnexpectedClosingBrace: 'SYN002',
  UnclosedParen: 'SYN003',
  MissingSemicolon: 'SYN004',
  ElseWithoutIf: 'SYN005',
  CaseOutsideSwitch: 'SYN006',
  ExpectedCondition: 'SYN007',
  ExpectedBlock: 'SYN008',
  ExpectedFunctionName: 'SYN009',
  ExpectedCalleeName: 'SYN010',
  ExpectedUntil: 'SYN011',
  UnexpectedToken: 'SYN012',
  MissingCaseColon: 'SYN013',
  EmptyLabel: 'SYN014',
  ExpectedForEachHeader: 'SYN015',
  DefaultOutsideSwitch: 'SYN016',

  // Semantic
  DuplicateFunction: 'SEM001',
  UnknownFunction: 'SEM002',
  BreakOutsideLoop: 'SEM003',
  ContinueOutsideLoop: 'SEM004',
  ReturnOutsideFunction: 'SEM005',
  UnreachableCode: 'SEM006',
  EmptyBlock: 'SEM007',
  EmptyDecisionLabel: 'SEM008',
  DirectRecursion: 'SEM009',
  IndirectRecursion: 'SEM010',
  DuplicateExplicitId: 'SEM011',
  UnusedFunction: 'SEM012',
  UnknownAnnotation: 'SEM013',
  InvalidAnnotationValue: 'SEM014',
  DuplicateDefaultCase: 'SEM015',
  EmptySwitch: 'SEM016',
  StatementAfterEnd: 'SEM017',
  MultipleStartNodes: 'SEM018',
  NoStartNode: 'SEM019',

  // Limits
  DocumentTooLarge: 'LIM001',
  NestingTooDeep: 'LIM002',
  TooManyNodes: 'LIM003',
  ExpansionDepthExceeded: 'LIM004',
} as const;

export type DiagnosticCodeValue = (typeof DiagnosticCode)[keyof typeof DiagnosticCode];

/** A related location — e.g. the first declaration when reporting a duplicate. */
export interface DiagnosticRelatedInformation {
  readonly message: string;
  readonly range: SourceRange;
}

export interface Diagnostic {
  readonly code: DiagnosticCodeValue;
  readonly severity: DiagnosticSeverity;
  /** English message. Translated at render time via the `i18n` layer. */
  readonly message: string;
  readonly range: SourceRange;
  /** Actionable hint, e.g. "Add a `}` to close the block opened on line 4." */
  readonly suggestion?: string;
  readonly related?: readonly DiagnosticRelatedInformation[];
  /**
   * Interpolation values for the i18n message template, keyed by placeholder
   * name. Lets translations reorder or re-word without losing the specifics.
   */
  readonly params?: Readonly<Record<string, string | number>>;
}

const SEVERITY_ORDER: Record<DiagnosticSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/** Errors first, then by position. Deterministic — tests rely on this. */
export function sortDiagnostics(diagnostics: readonly Diagnostic[]): Diagnostic[] {
  return [...diagnostics].sort((a, b) => {
    const bySeverity = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (bySeverity !== 0) return bySeverity;
    const byOffset = a.range.start.offset - b.range.start.offset;
    if (byOffset !== 0) return byOffset;
    return a.code.localeCompare(b.code);
  });
}

export function hasErrors(diagnostics: readonly Diagnostic[]): boolean {
  return diagnostics.some((d) => d.severity === 'error');
}

export function countBySeverity(
  diagnostics: readonly Diagnostic[],
): Record<DiagnosticSeverity, number> {
  const counts: Record<DiagnosticSeverity, number> = { error: 0, warning: 0, info: 0 };
  for (const d of diagnostics) counts[d.severity] += 1;
  return counts;
}

/**
 * Collapses diagnostics that share a code and a start offset. Error recovery
 * can legitimately produce the same complaint twice from different passes.
 */
export function dedupeDiagnostics(diagnostics: readonly Diagnostic[]): Diagnostic[] {
  const seen = new Set<string>();
  const out: Diagnostic[] = [];
  for (const d of diagnostics) {
    const key = `${d.code}:${d.range.start.offset}:${d.range.end.offset}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}
